// ─────────────────────────────────────────
// i18n/renderer.ts — DOM translation renderer
// Tauvior — Hardware, sistemas embarcados e conectividade industrial
// ─────────────────────────────────────────

import type {
  Translation,
  ServiceOffer,
  MethodStep,
  MethodDomain,
  GridComponent,
  TitledItem,
  PageHero,
  StageKind,
  FaqBlock,
  Ladder,
  HeroButton,
  SocialNetwork,
} from './types';
import { getPageKey } from './page';
import { withLocaleParam } from './detector';
import { BrandLockup, renderBrandLockups } from '../ts/brandLockup';

// ── Helpers ──────────────────────────────

function setText(selector: string, text: string): void {
  const el = document.querySelector(selector);
  if (el) el.textContent = text;
}

function setAttr(selector: string, attr: string, value: string): void {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function queryAll<T extends Element>(selector: string): T[] {
  return Array.from(document.querySelectorAll<T>(selector));
}

/**
 * Preenche um botão do hero, ou o remove quando está desligado.
 *
 * Remove em vez de esconder com `hidden`: os botões são `display:inline-flex`,
 * que ganha do `display:none` do atributo — o botão continuaria visível.
 */
function setButton(selector: string, button: HeroButton): void {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return;

  if (!button.enabled) {
    el.remove();
    return;
  }

  el.textContent = button.label;
}

/** Escapes text before it is interpolated into an innerHTML template. */
function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Section renderers ─────────────────────

function renderMeta(t: Translation): void {
  document.documentElement.lang = t.lang;
  document.documentElement.dir  = t.dir;
  setAttr('meta[property="og:locale"]', 'content', t.locale);

  // Título e description são por página; sem data-page conhecido o HTML
  // estático permanece, que é melhor do que anunciar a página errada.
  const page = getPageKey();
  if (!page) return;

  const { title, description } = t.meta[page];
  setText('title', title);
  setAttr('meta[name="description"]',        'content', description);
  setAttr('meta[property="og:title"]',       'content', title);
  setAttr('meta[property="og:description"]', 'content', description);
}

/**
 * O menu em si é construído pelo ts/nav.ts — aqui fica só o botão de CTA,
 * que é o único texto de nav presente no HTML estático.
 */
function renderNav(t: Translation): void {
  setText('[data-i18n="nav.cta"]', t.nav.cta);
}

// ── Blocos compartilhados ─────────────────

function setList(selector: string, html: string): void {
  const el = document.querySelector(selector);
  if (el) el.innerHTML = html;
}

/** Selo de estágio — o texto vem do JSON, a cor vem do `stage`. */
function stagePill(stage: StageKind, status: string): string {
  return `<span class="stage-pill" data-stage="${esc(stage)}"><span class="stage-pill-dot" aria-hidden="true"></span>${esc(status)}</span>`;
}

const ARROW_SVG = '<svg viewBox="0 0 14 12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M1 6h11M8 1.5L12.5 6 8 10.5"/></svg>';

/**
 * Perguntas frequentes em <details>: abre sem JS e o leitor de tela anuncia
 * o estado. `prefix` é o caminho do data-i18n (`home.faq`, `grid.faq`).
 */
function renderFaq(prefix: string, faq: FaqBlock): void {
  setText(`[data-i18n="${prefix}.sectionTag"]`, faq.sectionTag);
  setText(`[data-i18n="${prefix}.heading1"]`,   faq.heading1);
  setText(`[data-i18n="${prefix}.heading2"]`,   faq.heading2);
  setList(
    `[data-i18n-list="${prefix}.items"]`,
    faq.items
      .map(
        (item) => `
        <details class="faq-item">
          <summary class="faq-q"><span>${esc(item.question)}</span><span class="faq-icon" aria-hidden="true"></span></summary>
          <div class="faq-a">
            <p>${esc(item.answer)}</p>
            ${item.link ? `<a class="faq-link" href="${esc(withLocaleParam(item.link.href))}"><span>${esc(item.link.label)}</span>${ARROW_SVG}</a>` : ''}
          </div>
        </details>`,
      )
      .join(''),
  );
}

/** Escada de entrada: cada degrau leva a assinatura da solução (Grid / Advisory). */
function renderLadder(selector: string, ladder: Pick<Ladder, 'steps'>): void {
  setList(
    selector,
    ladder.steps
      .map(
        (step, i) => `
        <li class="ladder-step" data-line="${esc(step.line)}">
          <span class="ladder-n" aria-hidden="true">${String(i + 1).padStart(2, '0')}</span>
          ${BrandLockup({ line: step.line, markOnly: true })}
          <span class="ladder-name">${esc(step.name)}</span>
          <span class="ladder-text">${esc(step.text)}</span>
        </li>`,
      )
      .join(''),
  );
}

/**
 * Hero das páginas com quatro linhas. `prefix` é o caminho do data-i18n
 * (`home.hero`, `grid.hero`…) — o mesmo esqueleto de HTML serve a todas.
 */
function renderPageHero(prefix: string, hero: PageHero): void {
  setText(`[data-i18n="${prefix}.eyebrow"]`,     hero.eyebrow);
  setText(`[data-i18n="${prefix}.line1"]`,       hero.line1);
  setText(`[data-i18n="${prefix}.line2"]`,       hero.line2);
  setText(`[data-i18n="${prefix}.line3"]`,       hero.line3);
  setText(`[data-i18n="${prefix}.line4"]`,       hero.line4);
  setText(`[data-i18n="${prefix}.description"]`, hero.description);
  setButton(`[data-i18n="${prefix}.btnPrimary"]`,   hero.btnPrimary);
  setButton(`[data-i18n="${prefix}.btnSecondary"]`, hero.btnSecondary);
  setList(
    `[data-i18n-list="${prefix}.badges"]`,
    hero.badges.map((b) => `<span class="badge">${esc(b)}</span>`).join(''),
  );
}

function renderDomains(selector: string, domains: MethodDomain[]): void {
  setList(
    selector,
    domains
      .map(
        (domain) => `
        <div class="dom-card">
          <div>
            <div class="dom-name">${esc(domain.name)}</div>
            <div class="dom-sub">${esc(domain.detail)}</div>
          </div>
        </div>`
      )
      .join(''),
  );
}

function renderSteps(selector: string, steps: MethodStep[]): void {
  setList(
    selector,
    steps
      .map(
        (step) => `
        <div class="step-row">
          <span class="step-n">${esc(step.num)}</span>
          <div class="step-body">
            <h3>${esc(step.title)}</h3>
            <p>${esc(step.description)}</p>
          </div>
        </div>`
      )
      .join(''),
  );
}

function renderTitledItems(selector: string, items: TitledItem[], numbered = false): void {
  setList(
    selector,
    items
      .map(
        (item, i) => `
        <article class="titled-item">
          ${numbered ? `<span class="titled-num">${String(i + 1).padStart(2, '0')}</span>` : ''}
          <h3 class="titled-title">${esc(item.title)}</h3>
          <p class="titled-desc">${esc(item.description)}</p>
        </article>`
      )
      .join(''),
  );
}

function renderBulletList(selector: string, items: string[], className = 'check-item'): void {
  setList(selector, items.map((item) => `<li class="${className}">${esc(item)}</li>`).join(''));
}

/**
 * Formatos de contratação. Cada card carrega formato e entrega além da
 * descrição — sem prazo declarado e sem entrega nomeada, vira promessa vaga.
 */
function renderOffers(selector: string, items: ServiceOffer[]): void {
  setList(
    selector,
    items
      .map(
        (item) => `
      <article class="svc-card">
        <span class="svc-num">${esc(item.num)}</span>
        <h3 class="svc-title">${esc(item.title)}</h3>
        <p class="svc-desc">${esc(item.summary)}</p>
        <dl class="svc-meta">
          <dt>${esc(item.formatLabel)}</dt>
          <dd>${esc(item.format)}</dd>
          <dt>${esc(item.deliverableLabel)}</dt>
          <dd>${esc(item.deliverable)}</dd>
        </dl>
        <div class="svc-tags">${item.tags
          .map((tag) => `<span class="stag">${esc(tag)}</span>`)
          .join('')}</div>
      </article>`
      )
      .join(''),
  );
}

/**
 * Grid Node, Grid Edge e o software — `compact` é a versão da home, sem o
 * benefício. O nome vem completo do JSON ("Tauvior Grid Node"): a assinatura
 * gráfica (BrandLockup) é só da linha, não de cada componente.
 * A imagem conceitual do componente abre o card; o estágio de desenvolvimento
 * fica declarado uma vez por seção (faixa da /grid, selo da home).
 */
function renderGridComponents(selector: string, items: GridComponent[], compact = false): void {
  setList(
    selector,
    items
      .map(
        (item) => `
        <article class="gc-card" data-component="${esc(item.key)}">
          <figure class="gc-media">
            <img src="${esc(item.image)}" alt="${esc(item.imageAlt)}" loading="lazy" decoding="async" width="900" height="900" />
          </figure>
          <div class="gc-head">
            <h3 class="gc-name">${esc(item.name)}</h3>
          </div>
          <p class="gc-role">${esc(item.role)}</p>
          <p class="gc-desc">${esc(item.description)}</p>
          ${compact ? '' : `
          <dl class="gc-benefit">
            <dt>${esc(item.benefitLabel)}</dt>
            <dd>${esc(item.benefit)}</dd>
          </dl>`}
        </article>`
      )
      .join(''),
  );
}

/**
 * Preenche todos os elementos com o mesmo data-i18n. As páginas novas
 * repetem alguns rótulos (lockups, CTAs) em mais de um lugar.
 */
function setTextAll(key: string, text: string): void {
  queryAll<HTMLElement>(`[data-i18n="${key}"]`).forEach((el) => {
    el.textContent = text;
  });
}

// ── Home ──────────────────────────────────

/**
 * Caminho em notação de ponto dentro da tradução (`home.hero.title1`).
 * Índices de array funcionam como chave (`home.questions.items.0`).
 */
function lookup(t: Translation, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (node, key) =>
        node !== null && typeof node === 'object'
          ? (node as Record<string, unknown>)[key]
          : undefined,
      t,
    );
}

function isHeroButton(value: unknown): value is HeroButton {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as HeroButton).label === 'string' &&
    typeof (value as HeroButton).enabled === 'boolean'
  );
}

/**
 * Textos escalares da home. O HTML carrega a estrutura e o `data-i18n`
 * aponta a chave; `data-i18n-aria` faz o mesmo para o aria-label. Botões
 * `{ label, enabled }` seguem a regra do README: desligado, o elemento sai.
 */
function renderHomeText(t: Translation): void {
  queryAll<HTMLElement>('[data-i18n^="home."]').forEach((el) => {
    const value = lookup(t, el.dataset.i18n ?? '');
    if (typeof value === 'string') {
      el.textContent = value;
    } else if (isHeroButton(value)) {
      if (value.enabled) el.textContent = value.label;
      else el.remove();
    }
  });

  queryAll<HTMLElement>('[data-i18n-aria^="home."]').forEach((el) => {
    const value = lookup(t, el.dataset.i18nAria ?? '');
    if (typeof value === 'string') el.setAttribute('aria-label', value);
  });
}

const pad2 = (n: number): string => String(n + 1).padStart(2, '0');

function renderHomeLists(t: Translation): void {
  const { home } = t;

  // Só item habilitado aparece: prova sem dado autorizado fica no JSON.
  setList(
    '[data-i18n-list="home.proof.items"]',
    home.proof.items
      .filter((item) => item.enabled)
      .map(
        (item) => `
        <li class="hv-proof-item">
          <span class="hv-proof-value">${esc(item.value)}</span>
          <span class="hv-proof-label">${esc(item.label)}</span>
        </li>`,
      )
      .join(''),
  );

  setList(
    '[data-i18n-list="home.questions.items"]',
    home.questions.items
      .map(
        (question, i) => `
        <li class="hv-question">
          <span class="hv-num" aria-hidden="true">${pad2(i)}</span>
          <span class="hv-question-text">${esc(question)}</span>
        </li>`,
      )
      .join(''),
  );

  // As duas soluções lado a lado — a cor e a assinatura saem de `line`.
  setList(
    '[data-i18n-list="home.solutions.cards"]',
    home.solutions.cards
      .map(
        (card) => `
        <article class="hv-solution" data-line="${esc(card.line)}">
          <div class="hv-solution-top">
            <a class="hv-solution-brand" href="${esc(withLocaleParam(card.href))}">${BrandLockup({ line: card.line })}</a>
          </div>
          <p class="hv-solution-verb">${esc(card.verb)}</p>
          <p class="hv-solution-def">${esc(card.definition)}</p>
          <div class="hv-solution-cols">
            <div>
              <h3 class="hv-solution-label">${esc(card.audienceLabel)}</h3>
              <ul class="hv-solution-list">${card.audience.map((a) => `<li>${esc(a)}</li>`).join('')}</ul>
            </div>
            <div>
              <h3 class="hv-solution-label">${esc(card.deliverLabel)}</h3>
              <ul class="hv-solution-list">${card.deliver.map((d) => `<li>${esc(d)}</li>`).join('')}</ul>
            </div>
          </div>
          <div class="hv-actions hv-solution-actions">
            <a class="hv-btn hv-btn-primary" href="${esc(withLocaleParam(card.href))}">${esc(card.linkLabel)}</a>
          </div>
        </article>`,
      )
      .join(''),
  );
  renderLadder('[data-i18n-list="home.solutions.ladder.steps"]', home.solutions.ladder);

  // Case só entra com dado real autorizado; desligado, a seção sai inteira.
  const cases = document.getElementById('cases');
  if (!home.cases.enabled) {
    cases?.remove();
  } else {
    setList(
      '[data-i18n-list="home.cases.cards"]',
      home.cases.cards
        .map(
          (card) => `
          <li class="hv-case-card">
            <span class="hv-case-label">${esc(card.label)}</span>
            <p class="hv-case-text">${esc(card.text)}</p>
          </li>`,
        )
        .join(''),
    );
  }

  renderFaq('home.faq', home.faq);
}

function renderHome(t: Translation): void {
  renderHomeText(t);
  renderHomeLists(t);
}

// ── Grid ──────────────────────────────────

/** Como setList, mas para todos os elementos que compartilham o seletor. */
function setListAll(selector: string, html: string): void {
  queryAll<HTMLElement>(selector).forEach((el) => { el.innerHTML = html; });
}

function renderGridPage(t: Translation): void {
  const { grid } = t;
  renderPageHero('grid.hero', grid.hero);
  setAttr('[data-i18n-alt="grid.hero.imageAlt"]', 'alt', grid.hero.imageAlt);

  setText('[data-i18n="grid.stage.label"]', grid.stage.label);
  setText('[data-i18n="grid.stage.text"]',  grid.stage.text);
  setListAll('[data-i18n-list="grid.stage.pill"]', stagePill(grid.stage.stage, grid.stage.title));

  const { questions, layers, principle, verticals, components, pilot, cta } = grid;

  setText('[data-i18n="grid.questions.sectionTag"]', questions.sectionTag);
  setText('[data-i18n="grid.questions.heading1"]',   questions.heading1);
  setText('[data-i18n="grid.questions.heading2"]',   questions.heading2);
  setText('[data-i18n="grid.questions.lead"]',       questions.lead);
  setText('[data-i18n="grid.questions.closing"]',    questions.closing);
  setList(
    '[data-i18n-list="grid.questions.items"]',
    questions.items
      .map((q, i) => `<li class="q-item"><span class="q-num" aria-hidden="true">${pad2(i)}</span><span class="q-text">${esc(q)}</span></li>`)
      .join(''),
  );

  // Captar → Organizar → Decidir: cada camada é um componente da Grid.
  setText('[data-i18n="grid.layers.sectionTag"]', layers.sectionTag);
  setText('[data-i18n="grid.layers.heading1"]',   layers.heading1);
  setText('[data-i18n="grid.layers.heading2"]',   layers.heading2);
  setText('[data-i18n="grid.layers.lead"]',       layers.lead);
  setList(
    '[data-i18n-list="grid.layers.items"]',
    layers.items
      .map(
        (layer) => `
        <li class="lyr" data-layer="${esc(layer.key)}">
          <span class="lyr-num">${esc(layer.num)}</span>
          <h3 class="lyr-name">${esc(layer.name)}</h3>
          <p class="lyr-sub">${esc(layer.component)}</p>
          <p class="lyr-desc">${esc(layer.description)}</p>
          <p class="lyr-outcome">${esc(layer.outcome)}</p>
        </li>`,
      )
      .join(''),
  );
  setText('[data-i18n="grid.layers.progression.label"]', layers.progression.label);
  setList(
    '[data-i18n-list="grid.layers.progression.steps"]',
    layers.progression.steps
      .map(
        (step, i) => `
        <li class="prog-step">
          <span class="prog-label"><span class="prog-n" aria-hidden="true">${pad2(i)}</span>${esc(step.label)}</span>
          <p class="prog-example">${esc(step.example)}</p>
        </li>`,
      )
      .join(''),
  );

  setText('[data-i18n="grid.principle.sectionTag"]',  principle.sectionTag);
  setText('[data-i18n="grid.principle.title"]',       principle.title);
  setText('[data-i18n="grid.principle.body"]',        principle.body);
  setText('[data-i18n="grid.principle.caveatTitle"]', principle.caveatTitle);
  setText('[data-i18n="grid.principle.caveat"]',      principle.caveat);

  setText('[data-i18n="grid.components.sectionTag"]', components.sectionTag);
  setText('[data-i18n="grid.components.heading1"]',   components.heading1);
  setText('[data-i18n="grid.components.heading2"]',   components.heading2);
  setText('[data-i18n="grid.components.lead"]',       components.lead);
  renderGridComponents('[data-i18n-list="grid.components.items"]', components.items);
  setTextAll('grid.components.imageNote', components.imageNote);

  setText('[data-i18n="grid.verticals.sectionTag"]', verticals.sectionTag);
  setText('[data-i18n="grid.verticals.heading1"]',   verticals.heading1);
  setText('[data-i18n="grid.verticals.heading2"]',   verticals.heading2);
  setText('[data-i18n="grid.verticals.lead"]',       verticals.lead);
  setList(
    '[data-i18n-list="grid.verticals.features"]',
    verticals.features
      .map((feature) => `<li class="feature-board-item"><span class="feature-board-mark" aria-hidden="true"></span>${esc(feature)}</li>`)
      .join(''),
  );


  setText('[data-i18n="grid.pilot.sectionTag"]', pilot.sectionTag);
  setText('[data-i18n="grid.pilot.heading1"]',   pilot.heading1);
  setText('[data-i18n="grid.pilot.heading2"]',   pilot.heading2);
  setText('[data-i18n="grid.pilot.lead"]',       pilot.lead);
  renderSteps('[data-i18n-list="grid.pilot.steps"]', pilot.steps);

  renderFaq('grid.faq', grid.faq);

  setText('[data-i18n="grid.cta.title"]', cta.title);
  setText('[data-i18n="grid.cta.text"]',  cta.text);
  setText('[data-i18n="grid.cta.note"]',  cta.note);
  setTextAll('grid.cta.btnLabel',         cta.btnLabel);
}

// ── Advisory ──────────────────────────────

function renderAdvisoryPage(t: Translation): void {
  const { advisory } = t;
  renderPageHero('advisory.hero', advisory.hero);

  const { aside } = advisory;
  setText('[data-i18n="advisory.aside.label"]', aside.label);
  setText('[data-i18n="advisory.aside.quote"]', aside.quote);
  setText('[data-i18n="advisory.aside.note"]',  aside.note);

  const { areas, formatos, metodo, cta } = advisory;
  setText('[data-i18n="advisory.areas.sectionTag"]', areas.sectionTag);
  setText('[data-i18n="advisory.areas.heading1"]',   areas.heading1);
  setText('[data-i18n="advisory.areas.heading2"]',   areas.heading2);
  setText('[data-i18n="advisory.areas.lead"]',       areas.lead);
  setText('[data-i18n="advisory.areas.fitLabel"]',   areas.fitLabel);
  renderTitledItems('[data-i18n-list="advisory.areas.items"]', areas.items, true);
  renderBulletList('[data-i18n-list="advisory.areas.fit"]', areas.fit);

  setText('[data-i18n="advisory.formatos.sectionTag"]', formatos.sectionTag);
  setText('[data-i18n="advisory.formatos.heading1"]',   formatos.heading1);
  setText('[data-i18n="advisory.formatos.heading2"]',   formatos.heading2);
  setText('[data-i18n="advisory.formatos.lead"]',       formatos.lead);
  setText('[data-i18n="advisory.formatos.note"]',       formatos.note);
  renderOffers('[data-i18n-list="advisory.formatos.items"]', formatos.items);
  setText('[data-i18n="advisory.formatos.ladder.label"]', formatos.ladder.label);
  renderLadder('[data-i18n-list="advisory.formatos.ladder.steps"]', formatos.ladder);

  setText('[data-i18n="advisory.metodo.sectionTag"]', metodo.sectionTag);
  setText('[data-i18n="advisory.metodo.heading1"]',   metodo.heading1);
  setText('[data-i18n="advisory.metodo.heading2"]',   metodo.heading2);
  setText('[data-i18n="advisory.metodo.lead"]',       metodo.lead);
  renderSteps('[data-i18n-list="advisory.metodo.steps"]', metodo.steps);

  setText('[data-i18n="advisory.cta.title"]', cta.title);
  setText('[data-i18n="advisory.cta.text"]',  cta.text);
  setTextAll('advisory.cta.btnLabel',         cta.btnLabel);
}

// ── Sobre ─────────────────────────────────

function renderEmpresaPage(t: Translation): void {
  const { empresa } = t;
  renderPageHero('empresa.hero', empresa.hero);
  setText('[data-i18n="empresa.aside.label"]', empresa.aside.label);
  setText('[data-i18n="empresa.aside.quote"]', empresa.aside.quote);
  setText('[data-i18n="empresa.aside.note"]',  empresa.aside.note);

  const { trajetoria, principios, competencias } = empresa;
  setText('[data-i18n="empresa.trajetoria.sectionTag"]', trajetoria.sectionTag);
  setText('[data-i18n="empresa.trajetoria.heading1"]',   trajetoria.heading1);
  setText('[data-i18n="empresa.trajetoria.heading2"]',   trajetoria.heading2);
  setText('[data-i18n="empresa.trajetoria.brandLabel"]', trajetoria.brandLabel);
  setText('[data-i18n="empresa.trajetoria.foundation.label"]', trajetoria.foundation.label);
  setText('[data-i18n="empresa.trajetoria.foundation.text"]',  trajetoria.foundation.text);
  setList(
    '[data-i18n-list="empresa.trajetoria.paragraphs"]',
    trajetoria.paragraphs.map((p) => `<p>${esc(p)}</p>`).join(''),
  );

  // Tauvior → (Grid | Advisory) → ofertas; a engenharia é a base, não um ramo
  const { root, branches } = trajetoria;
  const branchHtml = (branch: (typeof branches)[number]): string => `
    <li class="bt-branch" data-line="${esc(branch.key)}">
      <a class="bt-card" href="${esc(withLocaleParam(branch.href))}">
        ${BrandLockup({ line: branch.key, markOnly: true })}
        <span class="bt-kind">${esc(branch.kind)}</span>
        <span class="bt-desc">${esc(branch.description)}</span>
      </a>
      <ul class="bt-children">
        ${branch.children
          .map((c) => `<li><a class="bt-chip" href="${esc(withLocaleParam(c.href))}">${esc(c.name)}</a></li>`)
          .join('')}
      </ul>
    </li>`;
  setList(
    '[data-i18n-list="empresa.trajetoria.brands"]',
    `
    <li class="bt-root">
      <a class="bt-card is-root" href="${esc(withLocaleParam(root.href))}">
        ${BrandLockup({ markOnly: true })}
        <span class="bt-desc">${esc(root.description)}</span>
      </a>
    </li>
    <li class="bt-branches" aria-label="${esc(trajetoria.brandLabel)}"><ul>${branches.map(branchHtml).join('')}</ul></li>`,
  );

  setText('[data-i18n="empresa.principios.sectionTag"]', principios.sectionTag);
  setText('[data-i18n="empresa.principios.heading1"]',   principios.heading1);
  setText('[data-i18n="empresa.principios.heading2"]',   principios.heading2);
  renderTitledItems('[data-i18n-list="empresa.principios.items"]', principios.items, true);

  setText('[data-i18n="empresa.competencias.sectionTag"]', competencias.sectionTag);
  setText('[data-i18n="empresa.competencias.heading1"]',   competencias.heading1);
  setText('[data-i18n="empresa.competencias.heading2"]',   competencias.heading2);
  setText('[data-i18n="empresa.competencias.lead"]',       competencias.lead);
  renderDomains('[data-i18n-list="empresa.competencias.domains"]', competencias.domains);
}

// ── Contato ───────────────────────────────

/** Os canais diretos saem da mesma lista do rodapé — um lugar só para trocar. */
function renderContatoPage(t: Translation): void {
  const page = t.contato;
  setText('[data-i18n="contato.eyebrow"]',       page.eyebrow);
  setText('[data-i18n="contato.line1"]',         page.line1);
  setText('[data-i18n="contato.line2"]',         page.line2);
  setText('[data-i18n="contato.description"]',   page.description);
  setText('[data-i18n="contato.pathsLabel"]',    page.pathsLabel);
  setText('[data-i18n="contato.channelsLabel"]', page.channelsLabel);
  setList(
    '[data-i18n-list="contato.paths"]',
    page.paths
      .map(
        (path) => `
        <li>
          <button type="button" class="path-card" data-interest="${esc(path.interest)}">
            <span class="path-title">${esc(path.title)}</span>
            <span class="path-text">${esc(path.text)}</span>
          </button>
        </li>`,
      )
      .join(''),
  );

  const channels = t.footer.social.filter((s) => s.href && SOCIAL_ICONS[s.network]);
  setList(
    '[data-i18n-list="contato.channels"]',
    channels
      .map((s) => {
        const newTab = /^https?:/i.test(s.href) ? ' target="_blank" rel="noopener"' : '';
        return `
        <li>
          <a class="channel-link" href="${esc(s.href)}"${newTab} aria-label="${esc(s.label)}">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">${SOCIAL_ICONS[s.network]}</svg>
            <span>${esc(s.text)}</span>
          </a>
        </li>`;
      })
      .join(''),
  );
}

function renderEdge(t: Translation): void {
  const { edge } = t;
  setText('[data-i18n="edge.sectionTag"]', edge.sectionTag);

  const blockquote = document.querySelector('.edge blockquote');
  if (blockquote) {
    const highlights = Array.isArray(edge.quoteHighlight)
      ? edge.quoteHighlight
      : [edge.quoteHighlight];

    const highlighted = highlights.reduce(
      (q, h) => q.replace(esc(h), `<em>${esc(h)}</em>`),
      esc(edge.quote)
    );
    blockquote.innerHTML = highlighted;
  }

  setText('[data-i18n="edge.body"]',   edge.body);
  setText('[data-i18n="edge.author"]', edge.author);
}

function renderSobre(t: Translation): void {
  const { sobre } = t;
  setText('[data-i18n="sobre.sectionTag"]', sobre.sectionTag);
  setText('[data-i18n="sobre.nameLine1"]',  sobre.nameLine1);
  setText('[data-i18n="sobre.nameLine2"]',  sobre.nameLine2);
  setText('[data-i18n="sobre.role"]',       sobre.role);
  setText('[data-i18n="sobre.body1"]',      sobre.body1);
  setText('[data-i18n="sobre.body2"]',      sobre.body2);
  setText('[data-i18n="sobre.note"]',       sobre.note);
  setAttr('[data-i18n-alt="sobre.imageAlt"]', 'alt', sobre.imageAlt);

  const credsContainer = document.querySelector('[data-i18n-list="sobre.credentials"]');
  if (credsContainer) {
    credsContainer.innerHTML = sobre.credentials
      .map((c) => `<li class="sobre-cred">${esc(c)}</li>`)
      .join('');
  }
}

/**
 * O bloco de agendamento só entra na página quando existe agenda para
 * apontar. Com `href` vazio o botão é removido e o formulário volta a ser
 * o caminho único — nunca um CTA de calendário que não leva a lugar nenhum.
 */
function renderScheduling(t: Translation): void {
  const { scheduling } = t.cta;
  const block = document.querySelector<HTMLElement>('.cta-schedule');
  if (!block) return;

  if (!scheduling.href) {
    block.remove();
    return;
  }

  const link = block.querySelector<HTMLAnchorElement>('.cta-schedule-link');
  if (link) {
    link.href        = scheduling.href;
    link.textContent = scheduling.label;
    link.target      = '_blank';
    link.rel         = 'noopener';
  }
  setText('.cta-schedule-note', scheduling.note);
}

function renderCta(t: Translation): void {
  const { cta } = t;
  setText('[data-i18n="cta.sectionTag"]',  cta.sectionTag);
  setText('[data-i18n="cta.heading1"]',    cta.heading1);
  setText('[data-i18n="cta.heading2"]',    cta.heading2);
  setText('[data-i18n="cta.description"]', cta.description);
  setText('[data-i18n="cta.btnPrimary"]',  cta.btnPrimary);
  setText('[data-i18n="cta.formAlt"]',     cta.formAlt);
  setText('[data-i18n="cta.note"]',        cta.note);
  renderScheduling(t);
}

function renderContactForm(t: Translation): void {
  const { form } = t.contact;
  setText('[data-i18n="contact.form.labelName"]',    form.labelName);
  setText('[data-i18n="contact.form.labelEmail"]',   form.labelEmail);
  setText('[data-i18n="contact.form.labelCompany"]', form.labelCompany);
  setText('[data-i18n="contact.form.labelInterest"]', form.labelInterest);

  // O assunto é um <select>: as opções vêm da tradução e o valor (grid,
  // advisory…) é estável entre idiomas — é ele que o pré-preenchimento usa.
  const interest = document.querySelector<HTMLSelectElement>('#contact-form select[name="interest"]');
  if (interest) {
    const selected = interest.value;
    interest.innerHTML = [
      `<option value="">${esc(form.interestPlaceholder)}</option>`,
      ...form.interestOptions.map(
        (opt) => `<option value="${esc(opt.value)}">${esc(opt.label)}</option>`,
      ),
    ].join('');
    interest.value = selected;
  }
  setText('[data-i18n="contact.form.labelMessage"]', form.labelMessage);
  setText('[data-i18n="contact.form.submitBtn"]',    form.submitBtn);
  setText('[data-i18n="contact.form.privacy"]',      form.privacy);

  setAttr('[data-i18n-placeholder="contact.form.placeholderName"]',    'placeholder', form.placeholderName);
  setAttr('[data-i18n-placeholder="contact.form.placeholderEmail"]',   'placeholder', form.placeholderEmail);
  setAttr('[data-i18n-placeholder="contact.form.placeholderCompany"]', 'placeholder', form.placeholderCompany);
  setAttr('[data-i18n-placeholder="contact.form.placeholderMessage"]', 'placeholder', form.placeholderMessage);

  const submitBtn = document.querySelector<HTMLButtonElement>('#contact-form [type="submit"]');
  if (submitBtn) submitBtn.textContent = form.submitBtn;
}

/* Ícones de linha, no mesmo traço dos outros ícones do site:
   24×24, currentColor, sem preenchimento.
   Exportado porque o botão flutuante (ts/whatsapp.ts) desenha o mesmo
   ícone do rodapé — dois traços separados divergiriam. */
export const SOCIAL_ICONS: Record<SocialNetwork, string> = {
  instagram: `
    <rect x="3.6" y="3.6" width="16.8" height="16.8" rx="4.8" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="4.1" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="16.6" cy="7.4" r="1.05" fill="currentColor"/>`,
  linkedin: `
    <rect x="3.6" y="3.6" width="16.8" height="16.8" rx="3.2" stroke="currentColor" stroke-width="1.5"/>
    <path d="M8.1 10.6v6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="8.1" cy="7.7" r="1.05" fill="currentColor"/>
    <path d="M11.9 16.6v-6M11.9 13.1c0-1.4 1-2.5 2.4-2.5s2.3 1 2.3 2.5v3.5"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  whatsapp: `
    <path d="M20.4 11.6a8.4 8.4 0 0 1-12.4 7.5l-4.4 1.3 1.3-4.4A8.4 8.4 0 1 1 20.4 11.6Z"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M9.3 8.4c-.6.6-.6 1.7 0 2.7.9 1.7 2.3 3.1 4 4 1 .5 2.1.5 2.7-.1"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  email: `
    <rect x="3.2" y="5.4" width="17.6" height="13.2" rx="2.4" stroke="currentColor" stroke-width="1.5"/>
    <path d="m4.6 7.7 6.2 4.5c.7.5 1.7.5 2.4 0l6.2-4.5"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
};

function renderFooterSocial(t: Translation): void {
  const container = document.querySelector<HTMLElement>('.footer-social');
  if (!container) return;

  // Link sem href não vira botão morto: some, como o CTA de agenda.
  const items = t.footer.social.filter((s) => s.href && SOCIAL_ICONS[s.network]);
  if (items.length === 0) {
    container.remove();
    return;
  }

  container.innerHTML = items
    .map((s) => {
      // mailto abre o cliente de e-mail na própria aba; só link http vira aba nova.
      const newTab = /^https?:/i.test(s.href) ? ' target="_blank" rel="noopener"' : '';
      return `
      <a class="footer-social-link" href="${esc(s.href)}"${newTab}
         aria-label="${esc(s.label)}" title="${esc(s.label)}" data-social="${esc(s.network)}">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">${SOCIAL_ICONS[s.network]}</svg>
      </a>`;
    })
    .join('');
}

function renderFooter(t: Translation): void {
  const { footer } = t;
  const linksContainer = document.querySelector('.footer-links');
  if (linksContainer) {
    linksContainer.innerHTML = footer.links
      .map((l) => `<a href="${esc(withLocaleParam(l.href))}">${esc(l.label)}</a>`)
      .join('');
  }
  renderFooterSocial(t);
  setText('.footer-copy', footer.copy);
}

function renderLangSwitcher(t: Translation): void {
  const switcher = document.getElementById('lang-switcher');
  if (!switcher) return;
  const altLocale = t.locale === 'pt-BR' ? 'en-US' : 'pt-BR';
  const altLabel  = t.locale === 'pt-BR' ? 'EN' : 'PT';
  const url       = new URL(window.location.href);
  url.searchParams.set('lang', altLocale);
  switcher.innerHTML = `
    <a href="${esc(url.toString())}" class="lang-switch-link" lang="${altLocale === 'pt-BR' ? 'pt' : 'en'}" hreflang="${altLocale}" aria-label="${esc(t.nav.langSwitchTo)}">
      ${altLabel}
    </a>
  `;
}

/**
 * Links internos escritos direto no HTML (`/grid`, `/advisory`) também
 * carregam o `?lang=` quando ele existe — os montados em runtime já passam
 * por withLocaleParam, e é idempotente, então aplicar de novo não duplica.
 */
function localizeStaticLinks(): void {
  queryAll<HTMLAnchorElement>('a[href^="/"]').forEach((link) => {
    const href = link.getAttribute('href');
    if (href) link.setAttribute('href', withLocaleParam(href));
  });
}

// ── Main export ───────────────────────────

/**
 * O que é comum a todas as páginas roda sempre; o resto é despachado pela
 * chave da página — cada renderer só procura os elementos da própria página.
 */
export function renderTranslation(t: Translation): void {
  renderMeta(t);
  renderNav(t);
  renderCta(t);
  renderContactForm(t);
  renderFooter(t);
  renderLangSwitcher(t);

  const page = getPageKey();
  renderBrandLockups();
  dispatchPage(page, t);
  localizeStaticLinks();
}

function dispatchPage(page: ReturnType<typeof getPageKey>, t: Translation): void {
  switch (page) {
    case 'home':
      renderHome(t);
      return;
    case 'grid':
      renderGridPage(t);
      return;
    case 'advisory':
      renderAdvisoryPage(t);
      return;
    case 'sobre':
      renderEmpresaPage(t);
      renderEdge(t);
      renderSobre(t);
      return;
    case 'contato':
      renderContatoPage(t);
      return;
  }
}
